/** Operator-facing labels — read-only case timeline; no payment/receipt/settlement wording. */

import type { CollectionCaseTimelineEventKind } from '@shared/collection/collection-case-timeline-types';
import { labelCollectionStageForCaseSummary } from './collection-case-summary-operator-labels';
import { COLLECTION_COMMITMENT_OPERATOR_LABELS } from './collection-commitment-operator-labels';

export const COLLECTION_CASE_TIMELINE_OPERATOR_LABELS = {
  sectionTitle: '수금 활동 타임라인',
  sectionSubtitle: '참고 · 관찰 · 시간순',
  loading: '타임라인을 불러오는 중…',
  loadFailed: '타임라인을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
  retryAction: '다시 불러오기',
  emptyNoCase: '등록된 수금 케이스 없음 · 아직 기록된 활동이 없습니다.',
  emptyNoEvents: '기록된 활동이 없습니다.',
  footerLiveRead: '실시간 조회 · 자동 조치 없음',
  footerAsOfPrefix: '조회 시점',
  approximateTimeSuffix: '(대략)',
  boundaryNoActions: '타임라인에서 연락·약속·수금 조치를 실행할 수 없습니다.',
  eventCaseOpened: '수금 케이스가 열렸습니다',
  eventContactFlowOpened: '채권 연락·팔로업이 시작되었습니다',
  eventContactEvidenceSubmitted: '연락 증빙이 제출되었습니다',
  eventCommitmentCreated: '후속 약속이 기록되었습니다',
  eventCommitmentCancelled: '약속이 취소되었습니다',
  eventCommitmentFulfilled: COLLECTION_COMMITMENT_OPERATOR_LABELS.statusFulfilledObservation,
  eventCommitmentBroken: COLLECTION_COMMITMENT_OPERATOR_LABELS.statusBrokenObservation,
  eventPaymentReceiptLinked: '수납 영수증 연결됨 (관찰)',
  metaPromisedDate: '약속일',
  metaCollectionStage: '팔로업 단계',
  metaContactFlowStatus: '연락 흐름 상태',
  metaEvidenceLabel: '증빙',
} as const;

const EVENT_KIND_SUMMARY: Record<CollectionCaseTimelineEventKind, string> = {
  case_opened: COLLECTION_CASE_TIMELINE_OPERATOR_LABELS.eventCaseOpened,
  contact_flow_opened: COLLECTION_CASE_TIMELINE_OPERATOR_LABELS.eventContactFlowOpened,
  contact_evidence_submitted:
    COLLECTION_CASE_TIMELINE_OPERATOR_LABELS.eventContactEvidenceSubmitted,
  commitment_created: COLLECTION_CASE_TIMELINE_OPERATOR_LABELS.eventCommitmentCreated,
  commitment_cancelled: COLLECTION_CASE_TIMELINE_OPERATOR_LABELS.eventCommitmentCancelled,
  commitment_fulfilled: COLLECTION_CASE_TIMELINE_OPERATOR_LABELS.eventCommitmentFulfilled,
  commitment_broken: COLLECTION_CASE_TIMELINE_OPERATOR_LABELS.eventCommitmentBroken,
  payment_receipt_linked: COLLECTION_CASE_TIMELINE_OPERATOR_LABELS.eventPaymentReceiptLinked,
};

export function summaryLabelForCollectionCaseTimelineKind(
  kind: CollectionCaseTimelineEventKind
): string {
  return EVENT_KIND_SUMMARY[kind];
}

export function formatCollectionCaseTimelineFooter(asOf: string): string {
  const formatted = formatCollectionCaseTimelineTimestamp(asOf);
  return `${COLLECTION_CASE_TIMELINE_OPERATOR_LABELS.footerLiveRead} · ${COLLECTION_CASE_TIMELINE_OPERATOR_LABELS.footerAsOfPrefix} ${formatted}`;
}

export function formatCollectionCaseTimelineTimestamp(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) {
    return iso;
  }
  return new Intl.DateTimeFormat('ko-KR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(parsed));
}

export function formatCollectionCaseTimelineRelativeTime(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) {
    return iso;
  }
  const deltaMs = Date.now() - parsed;
  const minutes = Math.round(deltaMs / 60_000);
  if (minutes < 1) {
    return '방금 전';
  }
  if (minutes < 60) {
    return `${minutes}분 전`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return `${hours}시간 전`;
  }
  return formatCollectionCaseTimelineTimestamp(iso);
}

export function labelCollectionStageForTimeline(stage: string | null | undefined): string {
  return labelCollectionStageForCaseSummary(stage);
}
